import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BizButton } from '../../../Core/Components/Mini/BizButton';
import BizInput from '../../../Core/Components/Mini/BizInput';
import { BizAutoComplete } from '../../../Core/Components/Mini/BizAutoComplete';
import {
	useGetTimereports,
	useGetUserInfo,
	useGetActivities,
	useGetFavoriteActivities,
	useSetFavoriteActivity,
	useCreateTimereport,
	useUpdateTimereport,
	useSubmitAllTimereports,
	useUpdateFlag,
} from '../../../Core/Hooks/useTimereport';
import { TimereportActivity, TimereportItem } from '../../../Core/Types/admin/timereport';
import { formatLocalDate } from '../../../Core/Utils/dateUtils';
import './TimereportWeekView.scss';
import BizSplitButton from '../../../Core/Components/Mini/BizSplitButton';

const getMonday = (date: Date): Date => {
	const d = new Date(date);
	const day = d.getDay();
	const diff = d.getDate() - day + (day === 0 ? -6 : 1);
	d.setDate(diff);
	d.setHours(0, 0, 0, 0);
	return d;
};

const addDays = (date: Date, days: number): Date => {
	const d = new Date(date);
	d.setDate(d.getDate() + days);
	return d;
};

type DayCell = {
	date: Date;
	dateStr: string;
	isLocked: boolean;
};

type RowDayState = {
	hours: number;
	comment: string;
};

type TimereportWeekViewProps = {
	selectedDate?: string;
	weekendEnabled?: boolean;
};

const TimereportWeekView = ({ selectedDate, weekendEnabled = false }: TimereportWeekViewProps) => {
	const { t } = useTranslation();
	const [weekStart, setWeekStart] = useState(() => getMonday(new Date()));
	const dayNames = [
		t('TIMEREPORT_USER.REPORT_VIEW.SHARED.WEEKDAYS.MON'),
		t('TIMEREPORT_USER.REPORT_VIEW.SHARED.WEEKDAYS.TUE'),
		t('TIMEREPORT_USER.REPORT_VIEW.SHARED.WEEKDAYS.WED'),
		t('TIMEREPORT_USER.REPORT_VIEW.SHARED.WEEKDAYS.THU'),
		t('TIMEREPORT_USER.REPORT_VIEW.SHARED.WEEKDAYS.FRI'),
		t('TIMEREPORT_USER.REPORT_VIEW.SHARED.WEEKDAYS.SAT'),
		t('TIMEREPORT_USER.REPORT_VIEW.SHARED.WEEKDAYS.SUN'),
	];

	useEffect(() => {
		if (selectedDate) {
			setWeekStart(getMonday(new Date(selectedDate + 'T00:00:00')));
		}
	}, [selectedDate]);

	const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);

	const { timereports, statusState } = useGetTimereports(
		formatLocalDate(weekStart),
		formatLocalDate(weekEnd)
	);
	const { userInfo } = useGetUserInfo();
	const { activities } = useGetActivities();
	const { favorites } = useGetFavoriteActivities();
	const { toggleFavorite } = useSetFavoriteActivity();
	const { create } = useCreateTimereport();
	const { update } = useUpdateTimereport();
	const { submitAll } = useSubmitAllTimereports();
	const { updateFlagDate } = useUpdateFlag();

	const flagDate = userInfo?.dateTimeReportFlag?.split('T')[0] ?? null;
	const employmentDate = userInfo?.dateEmployment?.split('T')[0] ?? null;

	// Build 7 day cells
	const dayCells: DayCell[] = useMemo(
		() =>
			Array.from({ length: 7 }, (_, i) => {
				const date = addDays(weekStart, i);
				const dateStr = formatLocalDate(date);
				const isBeforeEmployment = employmentDate != null && dateStr < employmentDate;
				return {
					date,
					dateStr,
					isLocked: isBeforeEmployment || (flagDate != null && dateStr <= flagDate),
				};
			}),
		[weekStart, flagDate, employmentDate]
	);

	// All items for the week
	const weekItems = useMemo(() => timereports ?? [], [timereports]);

	// Manually added activities (not favorites) for this week
	const [addedActivities, setAddedActivities] = useState<TimereportActivity[]>([]);

	useEffect(() => {
		setAddedActivities([]);
	}, [weekStart]);

	// Build rows: one per favorite activity + any non-favorite entries
	const rows = useMemo(() => {
		const result: { key: string; activity: TimereportActivity; items: (TimereportItem | null)[] }[] = [];
		const matchedItemIds = new Set<number>();

		if (favorites) {
			for (const fav of favorites) {
				const favItems = weekItems.filter(
					(t) => t.projectId === fav.projectId && t.activityId === fav.id
				);

				if (favItems.length === 0) {
					// Favorite with no entries this week — single empty row
					const dayItems = dayCells.map(() => null);
					result.push({ key: `${fav.projectId}-${fav.id}`, activity: fav, items: dayItems });
				} else {
					// Check if there are multiple items on any single day
					const dayMap = new Map<string, TimereportItem[]>();
					for (const item of favItems) {
						const dateKey = dayCells.find((dc) => item.date.startsWith(dc.dateStr))?.dateStr;
						if (dateKey) {
							if (!dayMap.has(dateKey)) dayMap.set(dateKey, []);
							dayMap.get(dateKey)!.push(item);
						}
						matchedItemIds.add(item.id);
					}

					const hasMultiplePerDay = [...dayMap.values()].some((items) => items.length > 1);

					if (!hasMultiplePerDay) {
						// Simple case: at most one item per day — single row
						const dayItems = dayCells.map(
							(dc) => favItems.find((t) => t.date.startsWith(dc.dateStr)) ?? null
						);
						result.push({ key: `${fav.projectId}-${fav.id}`, activity: fav, items: dayItems });
					} else {
						// Multiple items on same day — one row per item
						for (const item of favItems) {
							const dayItems = dayCells.map((dc) =>
								item.date.startsWith(dc.dateStr) ? item : null
							);
							result.push({
								key: `${fav.projectId}-${fav.id}-${item.id}`,
								activity: fav,
								items: dayItems,
							});
						}
					}
				}
			}
		}

		// Group remaining items by project+activity
		const remaining = weekItems.filter((t) => !matchedItemIds.has(t.id));
		const grouped = new Map<string, TimereportItem[]>();
		for (const item of remaining) {
			const gk = `${item.projectId}-${item.activityId}`;
			if (!grouped.has(gk)) grouped.set(gk, []);
			grouped.get(gk)!.push(item);
		}

		let extraIdx = 0;
		for (const [gk, items] of grouped) {
			const first = items[0];
			const act = activities?.find(
				(a) => a.projectId === first.projectId && a.id === first.activityId
			);
			if (!act) continue;

			const dayMap = new Map<string, TimereportItem[]>();
			for (const item of items) {
				const dateKey = dayCells.find((dc) => item.date.startsWith(dc.dateStr))?.dateStr;
				if (dateKey) {
					if (!dayMap.has(dateKey)) dayMap.set(dateKey, []);
					dayMap.get(dateKey)!.push(item);
				}
			}

			const hasMultiplePerDay = [...dayMap.values()].some((d) => d.length > 1);

			if (!hasMultiplePerDay) {
				const dayItems = dayCells.map(
					(dc) => items.find((t) => t.date.startsWith(dc.dateStr)) ?? null
				);
				result.push({ key: `extra-${gk}-${extraIdx++}`, activity: act, items: dayItems });
			} else {
				for (const item of items) {
					const dayItems = dayCells.map((dc) =>
						item.date.startsWith(dc.dateStr) ? item : null
					);
					result.push({ key: `extra-${gk}-${item.id}`, activity: act, items: dayItems });
				}
			}
		}

		// Manually added (non-favorite) activities
		const existingKeys = new Set(result.map((r) => `${r.activity.projectId}-${r.activity.id}`));
		for (const act of addedActivities) {
			const ak = `${act.projectId}-${act.id}`;
			if (existingKeys.has(ak)) continue;
			existingKeys.add(ak);

			const actItems = weekItems.filter(
				(t) => t.projectId === act.projectId && t.activityId === act.id
			);
			if (actItems.length === 0) {
				result.push({ key: `added-${ak}`, activity: act, items: dayCells.map(() => null) });
			} else {
				const dayItems = dayCells.map(
					(dc) => actItems.find((t) => t.date.startsWith(dc.dateStr)) ?? null
				);
				result.push({ key: `added-${ak}`, activity: act, items: dayItems });
			}
		}

		return result;
	}, [favorites, weekItems, activities, dayCells, addedActivities]);

	// Track editable state: key = `projectId-activityId`, value = array of 7 day states
	const [rowStates, setRowStates] = useState<Record<string, RowDayState[]>>({});
	const [rowComments, setRowComments] = useState<Record<string, string>>({});

	useEffect(() => {
		const newStates: Record<string, RowDayState[]> = {};
		const newComments: Record<string, string> = {};
		for (const row of rows) {
			newStates[row.key] = row.items.map((item) => ({
				hours: item?.units ?? 0,
				comment: item?.comment ?? '',
			}));
			const firstComment = row.items.find((item) => item?.comment)?.comment ?? '';
			newComments[row.key] = firstComment;
		}
		setRowStates(newStates);
		setRowComments(newComments);
	}, [rows, weekStart]);

	const updateCellState = (key: string, dayIndex: number, field: keyof RowDayState, value: string | number) => {
		setRowStates((prev) => {
			const row = [...(prev[key] ?? Array(7).fill({ hours: 0, comment: '' }))];
			row[dayIndex] = { ...row[dayIndex], [field]: value };
			return { ...prev, [key]: row };
		});
	};

	// Add-new-entry state
	type ActivityOption = { Id: string; Name: string; activity: TimereportActivity };
	const [addActivity, setAddActivity] = useState<ActivityOption | null>(null);

	const rowActivityKeys = useMemo(
		() => {
			const keys = new Set(rows.map((r) => `${r.activity.projectId}-${r.activity.id}`));
			for (const a of addedActivities) keys.add(`${a.projectId}-${a.id}`);
			return keys;
		},
		[rows, addedActivities]
	);

	const activityOptions: ActivityOption[] = useMemo(() => {
		if (!activities) return [];
		return activities
			.filter((a) => !rowActivityKeys.has(`${a.projectId}-${a.id}`))
			.map((a) => ({
				Id: `${a.projectId}-${a.id}`,
				Name: `${a.customer || '—'} → ${a.projectName} → ${a.name}`,
				activity: a,
			}));
	}, [activities, rowActivityKeys]);

	const visibleDayCount = weekendEnabled ? 7 : 5;

	const handleAdd = () => {
		if (!addActivity) return;
		setAddedActivities((prev) => [...prev, addActivity.activity]);
		setAddActivity(null);
	};

	const handleSave = () => {
		for (const row of rows) {
			const states = rowStates[row.key];
			if (!states) continue;

			const comment = rowComments[row.key] ?? '';
			const defaultTypeId = row.activity.timereportTypes.length > 0
				? row.activity.timereportTypes[0].timereportTypeId
				: 0;

			for (let i = 0; i < 7; i++) {
				const dc = dayCells[i];
				if (dc.isLocked) continue;
				const state = states[i];
				const item = row.items[i];

				if (item) {
					if (state.hours !== item.units || comment !== item.comment) {
						update({
							id: item.id,
							projectId: item.projectId,
							activityId: item.activityId,
							typeId: item.typeId,
							objectType: item.objectType,
							objectId: item.objectId,
							hours: state.hours,
							date: dc.dateStr,
							comment,
						});
					}
				} else if (state.hours > 0) {
					create({
						projectId: row.activity.projectId,
						activityId: row.activity.id,
						typeId: defaultTypeId,
						objectType: null,
						objectId: null,
						hours: state.hours,
						date: dc.dateStr,
						comment,
					});
				}
			}
		}
	};

	const handleSubmit = () => {
		const endDate = formatLocalDate(weekEnd);
		submitAll(endDate);
		updateFlagDate(endDate);
	};

	const handleSubmitWeek = () => {
		const sunday = addDays(weekStart, 6);
		const endDate = formatLocalDate(sunday);
		submitAll(endDate);
		updateFlagDate(endDate);
	};

	const handleToggleFavorite = (activity: TimereportActivity) => {
		toggleFavorite(activity.projectId, activity.id, !activity.isFavorite);
	};

	const handlePrevWeek = () => setWeekStart((prev) => addDays(prev, -7));
	const handleNextWeek = () => setWeekStart((prev) => addDays(prev, 7));
	const handleToday = () => setWeekStart(getMonday(new Date()));

	const weekLabel = `${formatLocalDate(weekStart)} – ${formatLocalDate(weekEnd)}`;

	// Day totals
	const dayTotals = useMemo(() => {
		return dayCells.map((dc) => {
			return weekItems
				.filter((t) => t.date.startsWith(dc.dateStr))
				.reduce((sum, t) => sum + t.units, 0);
		});
	}, [dayCells, weekItems]);

	const weekTotal = dayTotals.slice(0, visibleDayCount).reduce((sum, t) => sum + t, 0);

	const onSaveClick = (key: string) => {
		if (key === 'save') {
			handleSave();
		} else if (key === 'submit') {
			handleSave();
			handleSubmit();
		} else if (key === 'submitweek') {
			handleSave();
			handleSubmitWeek();
		}
	};

	return (
		<div className='biz-timereport-week-view'>
			<div className='biz-timereport-week-view__navigation'>
				<BizButton text='◀' onClick={handlePrevWeek} />
				<BizButton text={t('TIMEREPORT_USER.REPORT_VIEW.SHARED.ACTIONS.TODAY')} onClick={handleToday} />
				<span className='biz-timereport-week-view__week-label'>{weekLabel}</span>
				<BizButton text='▶' onClick={handleNextWeek} />
			</div>

			{statusState === 'LOADING' && <p>{t('TIMEREPORT_USER.REPORT_VIEW.SHARED.STATE.LOADING')}</p>}

			{/* Add new entry row */}
			<div className='biz-timereport-week-view__add-row'>
				<div className='biz-timereport-week-view__add-row-select'>
					<BizAutoComplete
						options={activityOptions}
						value={addActivity ?? undefined}
						onChange={(opt) => setAddActivity(opt)}
						componentLabel={t('TIMEREPORT_USER.REPORT_VIEW.SHARED.PLACEHOLDERS.SEARCH_ACTIVITY')}
					/>
				</div>
				<BizButton
					text={t('TIMEREPORT_USER.REPORT_VIEW.SHARED.ACTIONS.ADD')}
					onClick={handleAdd}
					icon='fa-solid fa-plus'
					disabled={!addActivity}
				/>
			</div>

			{/* Week table */}
			<div
				className='biz-timereport-week-view__table'
				style={{ '--day-count': visibleDayCount } as React.CSSProperties}
			>
				{/* Header row */}
				<div className='biz-timereport-week-view__table-header'>
					<div className='biz-timereport-week-view__col-fav'></div>
					<div className='biz-timereport-week-view__col-customer'>{t('TIMEREPORT_USER.REPORT_VIEW.SHARED.TABLE.CUSTOMER')}</div>
					<div className='biz-timereport-week-view__col-project'>{t('TIMEREPORT_USER.REPORT_VIEW.SHARED.TABLE.PROJECT')}</div>
					<div className='biz-timereport-week-view__col-activity'>{t('TIMEREPORT_USER.REPORT_VIEW.SHARED.TABLE.ACTIVITY')}</div>
					{dayCells.slice(0, visibleDayCount).map((dc, i) => (
						<div
							key={dc.dateStr}
							className={`biz-timereport-week-view__col-day-header${i >= 5 ? ' biz-timereport-week-view__col-day-header--weekend' : ''}${dc.isLocked ? ' biz-timereport-week-view__col-day-header--locked' : ''}`}
						>
							{dayNames[i]}
							<span className='biz-timereport-week-view__col-day-date'>
								{dc.date.getDate()}/{dc.date.getMonth() + 1}
							</span>
						</div>
					))}
					<div className='biz-timereport-week-view__col-total'>{t('TIMEREPORT_USER.REPORT_VIEW.SHARED.TABLE.TOTAL')}</div>
					<div className='biz-timereport-week-view__col-comment'>{t('TIMEREPORT_USER.REPORT_VIEW.SHARED.TABLE.COMMENT')}</div>
				</div>

				{/* Data rows */}
				{rows.map((row) => {
					const states = rowStates[row.key] ?? Array(7).fill({ hours: 0, comment: '' });
					const rowTotal = states.reduce((sum, s) => sum + (s.hours || 0), 0);

					return (
						<div key={row.key} className='biz-timereport-week-view__table-row'>
							<div className='biz-timereport-week-view__col-fav'>
								<span
									className='biz-timereport-week-view__fav-toggle'
									onClick={() => handleToggleFavorite(row.activity)}
									title={
										row.activity.isFavorite
											? t('TIMEREPORT_USER.REPORT_VIEW.SHARED.FAVORITES.REMOVE')
											: t('TIMEREPORT_USER.REPORT_VIEW.SHARED.FAVORITES.ADD')
									}
								>
									{row.activity.isFavorite ? '★' : '☆'}
								</span>
							</div>
							<div className='biz-timereport-week-view__col-customer'>
								{row.activity.customer || '—'}
							</div>
							<div className='biz-timereport-week-view__col-project'>
								{row.activity.projectName}
							</div>
							<div className='biz-timereport-week-view__col-activity'>
								{row.activity.name}
							</div>
							{dayCells.slice(0, visibleDayCount).map((dc, i) => {
								const item = row.items[i];
								const cellDisabled =
									dc.isLocked || (item?.isSubmitted ?? false) || (item?.isAttested ?? false);
								return (
									<div
										key={dc.dateStr}
										className={`biz-timereport-week-view__col-day${cellDisabled ? ' biz-timereport-week-view__col-day--disabled' : ''}`}
									>
										<BizInput
											type='number'
											value={String(states[i]?.hours || '')}
											onChange={(e) =>
												updateCellState(row.key, i, 'hours', parseFloat(e.target.value) || 0)
											}
											min='0'
											max='24'
											readonly={cellDisabled}
										/>
									</div>
								);
							})}
							<div className='biz-timereport-week-view__col-total'>
								{rowTotal > 0 ? `${rowTotal}h` : ''}
							</div>
							<div className='biz-timereport-week-view__col-comment'>
								<BizInput
									value={rowComments[row.key] ?? ''}
									onChange={(e) =>
										setRowComments((prev) => ({ ...prev, [row.key]: e.target.value }))
									}
									placeholder={t('TIMEREPORT_USER.REPORT_VIEW.SHARED.PLACEHOLDERS.COMMENT')}
								/>
							</div>
						</div>
					);
				})}

				{rows.length === 0 && (
					<div className='biz-timereport-week-view__empty'>
						{t('TIMEREPORT_USER.REPORT_VIEW.SHARED.EMPTY.NO_FAVORITES')}
					</div>
				)}

				{/* Totals row */}
				<div className='biz-timereport-week-view__table-footer'>
					<div className='biz-timereport-week-view__col-fav'></div>
					<div className='biz-timereport-week-view__col-customer'></div>
					<div className='biz-timereport-week-view__col-project'></div>
					<div className='biz-timereport-week-view__col-activity biz-timereport-week-view__footer-label'>
						{t('TIMEREPORT_USER.REPORT_VIEW.SHARED.TABLE.TOTAL')}
					</div>
					{dayTotals.slice(0, visibleDayCount).map((total, i) => (
						<div key={i} className='biz-timereport-week-view__col-day biz-timereport-week-view__footer-total'>
							{total > 0 ? `${total}h` : ''}
						</div>
					))}
					<div className='biz-timereport-week-view__col-total biz-timereport-week-view__footer-total'>
						{weekTotal}h
					</div>
					<div className='biz-timereport-week-view__col-comment'></div>
				</div>
			</div>

			{/* Action buttons */}
			<div className='biz-timereport-week-view__actions'>
				{/* <BizButton
					text='Save'
					onClick={handleSave}
					icon='far fa-save'
					state={updateStatus}
				/>
				<BizButton
					text='Submit'
					onClick={handleSubmit}
					icon='far fa-check'
					state={submitStatus}
					disabled={weekItems.filter((t) => !t.isSubmitted).length === 0}
				/> */}
				<BizSplitButton options={[
						{
							Key: 'save',
							Value: t('TIMEREPORT_USER.REPORT_VIEW.SHARED.ACTIONS.SAVE')
						},
						{
							Key: 'submit',
							Value: t('TIMEREPORT_USER.REPORT_VIEW.SHARED.ACTIONS.SUBMIT_REPORTS')
						},
						{
							Key: 'submitweek',
							Value: t('TIMEREPORT_USER.REPORT_VIEW.SHARED.ACTIONS.SUBMIT_AND_CLOSE_WEEK')
						}
					]} handleClick={onSaveClick} isLead={true} />
			</div>
		</div>
	);
};

export default TimereportWeekView;
